import { useState } from "react";
import { useRoute, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, ArrowLeft, Plus, Trash2, DollarSign } from "lucide-react";
import { DateCell } from "@/components/DateCell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface PriceListLine {
  id: string;
  priceListId: string;
  productId: string;
  price: string;
  minQuantity: number;
  priceUom: string;
  validFrom: string | null;
  validTo: string | null;
}

interface PriceList {
  id: string;
  name: string;
  currencyCode: string;
  status: string;
  validFrom: string | null;
  validTo: string | null;
  notes: string | null;
  lines: PriceListLine[];
}

interface Product {
  id: string;
  name: string;
}

export default function PriceListDetailPage() {
  const { sessionId } = useAuth();
  const [, params] = useRoute("/price-lists/:id");
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newLine, setNewLine] = useState({ productId: "", price: "", minQuantity: "1", priceUom: "EA" });

  const id = params?.id;

  const { data: pl, isLoading } = useQuery<PriceList>({
    queryKey: ["/api/price-lists", id],
    queryFn: () => api.get<PriceList>(`/price-lists/${id}`, sessionId),
    enabled: !!id,
  });

  const { data: productsData } = useQuery<{ products: Product[]; total: number }>({
    queryKey: ["/api/products", { limit: 200 }],
    queryFn: () => api.get<{ products: Product[]; total: number }>(`/products?limit=200`, sessionId),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: Partial<PriceList>) => api.patch<PriceList>(`/price-lists/${id}`, payload, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/price-lists", id] });
      toast({ title: "Price list updated" });
    },
  });

  const addLineMutation = useMutation({
    mutationFn: (payload: { productId: string; price: string; minQuantity: number; priceUom: string }) =>
      api.post<PriceListLine>(`/price-lists/${id}/lines`, payload, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/price-lists", id] });
      setNewLine({ productId: "", price: "", minQuantity: "1", priceUom: "EA" });
      toast({ title: "Price break added" });
    },
  });

  const deleteLineMutation = useMutation({
    mutationFn: (lineId: string) => api.delete(`/price-list-lines/${lineId}`, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/price-lists", id] });
      toast({ title: "Price break removed" });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!pl) {
    return <div className="p-8 text-center text-muted-foreground">Price list not found</div>;
  }

  const products = productsData?.products || [];

  return (
    <div className="space-y-6">
      <PageHeader
        title={pl.name}
        description={pl.status || "active"}
        actions={
          <Button variant="outline" onClick={() => setLocation("/price-lists")} data-testid="button-back">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Status</CardTitle>
          </CardHeader>
          <CardContent>
            <Select
              value={pl.status || "active"}
              onValueChange={(val) => updateMutation.mutate({ status: val })}
            >
              <SelectTrigger data-testid="select-pl-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Valid From</CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <DateCell value={pl.validFrom} fallback="Not set" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Valid To</CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <DateCell value={pl.validTo} fallback="Not set" />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Price Breaks ({pl.lines?.length || 0} lines)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Min Qty</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>UOM</TableHead>
                <TableHead>Valid From</TableHead>
                <TableHead>Valid To</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(pl.lines || []).map((line) => {
                const product = products.find((p) => p.id === line.productId);
                return (
                  <TableRow key={line.id} data-testid={`row-pll-${line.id}`}>
                    <TableCell className="font-medium">{product?.name || line.productId}</TableCell>
                    <TableCell>{line.minQuantity}</TableCell>
                    <TableCell>${Number(line.price).toFixed(2)}</TableCell>
                    <TableCell>{line.priceUom || "EA"}</TableCell>
                    <TableCell className="text-muted-foreground"><DateCell value={line.validFrom} /></TableCell>
                    <TableCell className="text-muted-foreground"><DateCell value={line.validTo} /></TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteLineMutation.mutate(line.id)}
                        data-testid={`button-delete-pll-${line.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {(!pl.lines || pl.lines.length === 0) && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No price breaks defined. Add products with quantity-based pricing below.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <div className="mt-4 flex gap-2 items-end border-t pt-4">
            <div className="flex-1">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Product</label>
              <Select value={newLine.productId} onValueChange={(v) => setNewLine({ ...newLine, productId: v })}>
                <SelectTrigger data-testid="select-new-pll-product">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-24">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Min Qty</label>
              <Input
                type="number"
                min="1"
                value={newLine.minQuantity}
                onChange={(e) => setNewLine({ ...newLine, minQuantity: e.target.value })}
                data-testid="input-new-pll-min-qty"
              />
            </div>
            <div className="w-32">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Price</label>
              <Input
                type="number"
                step="any"
                min={0}
                placeholder="0.00"
                value={newLine.price}
                onChange={(e) => setNewLine({ ...newLine, price: e.target.value })}
                data-testid="input-new-pll-price"
              />
            </div>
            <Button
              onClick={() => {
                if (!newLine.productId || !newLine.price) return;
                addLineMutation.mutate({
                  productId: newLine.productId,
                  price: newLine.price,
                  minQuantity: parseInt(newLine.minQuantity) || 1,
                  priceUom: newLine.priceUom,
                });
              }}
              disabled={!newLine.productId || !newLine.price || addLineMutation.isPending}
              data-testid="button-add-pll"
            >
              {addLineMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Add
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
